import React, { useState, useEffect, useRef } from 'react'
import { Helmet } from "react-helmet";
import 'react-image-gallery/styles/css/image-gallery.css';
import Navbar from './components/Navbar'
import Footer from './components/Footer'
import portrait from '../assets/home/portraits6.jpg'
import wedding from '../assets/home/wedding5.jpg'
import documentary from '../assets/home/documentary.jpg'
import commercial from '../assets/home/commercial.jpg'
import event from '../assets/home/event1.jpg'
import '../style.css'

// ─── SERVICE BLOCKS ───────────────────────────────────────────────────────────

const services = [
  {
    href: "/portfolio/wedding",
    image: wedding,
    alt: "UK Wedding Photography and Cinematography",
    label: "Wedding",
    title: "Wedding Photography & Film",
    body: "No staged moments. Unobtrusive, documentary-style coverage for luxury UK weddings. We don't direct your day; we capture it exactly as it felt.",
    cta: "Explore Weddings",
  },
  {
    href: "/portfolio/portrait",
    image: portrait,
    alt: "UK Portrait Photography and Creative Headshots",
    label: "Portrait",
    title: "Portraiture & Creative Headshots",
    body: "Authentic representation. Clean, editorial-style portraits and corporate headshots. We eliminate the pressure of the camera so your actual personality translates.",
    cta: "Explore Portraits",
  },
  {
    href: "/portfolio/documentary",
    image: documentary,
    alt: "UK Documentary Photography and Photojournalism",
    label: "Documentary",
    title: "Documentary & Photojournalism",
    body: "Truth in context. Immersive visual essays and long-term field documentation. We embed ourselves in your cause to produce imagery that speaks volumes.",
    cta: "Explore Documentary",
  },
  {
    href: "/portfolio/commercial",
    image: commercial,
    alt: "UK Commercial and Brand Photography",
    label: "Commercial",
    title: "Commercial & Brand Photography",
    body: "Visuals that actually sell. High-impact corporate campaigns, executive profiling, and strategic brand documentation engineered for B2B growth.",
    cta: "Explore Commercial",
  },
  {
    href: "/portfolio/event",
    image: event,
    alt: "UK Event Photography and Coverage",
    label: "Event",
    title: "Event Photography & Coverage",
    body: "Every milestone deserves to be documented. From corporate conferences to cultural celebrations, we capture the energy, atmosphere, and key moments of your event.",
    cta: "Explore Events",
  },
];

// ─── ANIMATED COUNTER ─────────────────────────────────────────────────────────

const useInView = (ref) => {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [ref]);
  return inView;
};

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────

const PortfolioAll = () => {
  const [setImages] = useState([]);
  const statsRef = useRef(null);
  const inView = useInView(statsRef);

  useEffect(() => {
    const fetchImages = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/upload/`);
        if (!response.ok) { console.error('Error fetching images'); return; }
        const data = await response.json();
        setImages(
          data.map((image) => ({
            original: image.image,
            thumbnail: image.image,
          }))
        );
      } catch (error) {
        console.error('Error fetching images:', error);
      }
    };
    fetchImages();
  }, [setImages]);

  return (
    <>
      <Helmet>
        <title>Glintz Photography Portfolio | UK Wedding, Portrait, Commercial & Documentary</title>
        <meta name="description" content="Browse the Glintz Photography portfolio: Manchester-based experts in wedding, portrait, commercial & documentary photography. Premium UK visual storytelling." />
      </Helmet>

      <Navbar />

      {/* ── HERO ─────────────────────────────────────────────────────────────── */}
      <section className="relative h-screen min-h-[600px] overflow-hidden bg-black flex items-end">

        {/* Split background panels */}
        <div className="absolute inset-0 grid grid-cols-2 md:grid-cols-4 opacity-60">
          <div
            className="bg-cover bg-center"
            style={{ backgroundImage: `url(${wedding})` }}
          />
          <div
            className="bg-cover bg-center hidden md:block"
            style={{ backgroundImage: `url(${commercial})` }}
          />
          <div
            className="bg-cover bg-center"
            style={{ backgroundImage: `url(${portrait})` }}
          />
          <div
            className="bg-cover bg-center hidden md:block"
            style={{ backgroundImage: `url(${documentary})` }}
          />
        </div>

        {/* Gradient overlays */}
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-black/20 z-10" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/40 via-transparent to-black/40 z-10" />

        {/* Thin horizontal rule accent */}
        <div className="absolute top-1/2 left-0 right-0 h-px bg-white/10 z-10" />

        {/* Hero text */}
        <div className="relative z-20 w-full xl:px-44 lg:px-24 px-6 pb-16 xl:pb-24">
          <p
            className="text-white/50 uppercase tracking-[.4em] text-xs mb-5"
            style={{ fontFamily: "Muli, sans-serif" }}
          >
            Manchester &amp; Across the UK
          </p>
          <h1
            className="text-white uppercase xl:text-6xl lg:text-5xl md:text-4xl text-3xl leading-[1.05] tracking-[.08em] mb-6 max-w-4xl"
          >
            Premium Visual<br />Storytelling Across<br />Manchester and the UK
          </h1>
          <p
            className="text-white/70 text-sm md:text-base max-w-xl leading-relaxed mb-10"
            style={{ fontFamily: "Muli, sans-serif" }}
          >
            Authentic documentation and strategic visual assets. We tailor our photography and cinematography to match the exact scale and emotion of your project.
          </p>
          <div className="flex flex-wrap gap-4">
            <a
              href="/contact"
              className="bg-white text-black px-8 py-3 text-xs tracking-[.25em] uppercase hover:bg-white/90 transition-all"
              style={{ fontFamily: "Muli, sans-serif" }}
            >
              Book a Session
            </a>
            <a
              href="#services"
              className="border border-white/60 text-white px-8 py-3 text-xs tracking-[.25em] uppercase hover:border-white hover:bg-white/10 transition-all"
              style={{ fontFamily: "Muli, sans-serif" }}
            >
              View Our Work
            </a>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 right-8 xl:right-44 z-20 flex flex-col items-center gap-2 opacity-50">
          <span className="text-white text-[10px] tracking-[.3em] uppercase rotate-90 origin-center" style={{ fontFamily: "Muli, sans-serif" }}>Scroll</span>
          <div className="w-px h-10 bg-white/50" />
        </div>
      </section>

      {/* ── SERVICE GRID ─────────────────────────────────────────────────────── */}
      <section id="services" className="bg-black">
        <div className="grid grid-cols-1 md:grid-cols-2">
          {services.map((service, i) => (
            <a
              key={service.href}
              href={service.href}
              className="group relative overflow-hidden block"
              style={{ aspectRatio: "4/3" }}
            >
              {/* Image */}
              <img
                src={service.image}
                alt={service.alt}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
              />

              {/* Base dark overlay */}
              <div className="absolute inset-0 bg-black/40 group-hover:bg-black/60 transition-colors duration-500" />

              {/* Bottom gradient */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent" />

              {/* Index number */}
              <span
                className="absolute top-6 right-6 text-white/20 text-6xl font-light leading-none select-none"
              >
                0{i + 1}
              </span>

              {/* Content */}
              <div className="absolute inset-0 flex flex-col justify-end p-6 xl:p-10">
                <p
                  className="text-white/40 uppercase tracking-[.35em] text-[10px] mb-2"
                  style={{ fontFamily: "Muli, sans-serif" }}
                >
                  {service.label}
                </p>
                <h2 className="text-white uppercase text-xl xl:text-2xl tracking-[.08em] leading-tight mb-3">
                  {service.title}
                </h2>
                <p
                  className="text-white/60 text-sm leading-relaxed mb-5 max-w-sm translate-y-2 opacity-0 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500"
                  style={{ fontFamily: "Muli, sans-serif" }}
                >
                  {service.body}
                </p>
                <div className="flex items-center gap-3">
                  <span
                    className="text-white text-xs uppercase tracking-[.25em]"
                    style={{ fontFamily: "Muli, sans-serif" }}
                  >
                    {service.cta}
                  </span>
                  <span className="text-white/60 transition-transform duration-300 group-hover:translate-x-2">→</span>
                </div>
              </div>
            </a>
          ))}
        </div>
      </section>

      {/* ── WORLDWIDE CTA ─────────────────────────────────────────────────────── */}
      <section className="bg-black border-t border-white/10 xl:px-44 lg:px-24 px-6 py-24 xl:py-32">
        <div className="grid xl:grid-cols-2 gap-16 items-center">
          <div>
            <p
              className="text-white/40 uppercase tracking-[.4em] text-xs mb-5"
              style={{ fontFamily: "Muli, sans-serif" }}
            >
              Our Reach
            </p>
            <h2 className="text-white uppercase xl:text-5xl lg:text-4xl text-3xl tracking-[.08em] leading-tight mb-6">
              Rooted in Manchester.<br />Commissioned Worldwide.
            </h2>
            <p
              className="text-white/60 text-sm leading-relaxed mb-10 max-w-lg"
              style={{ fontFamily: "Muli, sans-serif" }}
            >
              Our bags are always packed. From our hub in Manchester, we frequently travel for destination weddings and international B2B campaigns. Let's talk about where you need us next.
            </p>
            <a
              href="/contact"
              className="inline-block bg-white text-black px-10 py-4 text-xs tracking-[.3em] uppercase hover:bg-white/90 transition-all"
              style={{ fontFamily: "Muli, sans-serif" }}
            >
              Request a Consultation
            </a>
          </div>

          {/* Stats */}
          <div ref={statsRef} className="grid grid-cols-2 gap-px bg-white/10">
            {[
              { value: "500+", label: "Sessions Delivered" },
              { value: "12+", label: "Countries Travelled" },
              { value: "8+", label: "Years of Practice" },
              { value: "100%", label: "Client Satisfaction" },
            ].map((stat) => (
              <div
                key={stat.label}
                className="bg-black px-8 py-10 flex flex-col gap-2"
                style={{
                  opacity: inView ? 1 : 0,
                  transform: inView ? "translateY(0)" : "translateY(20px)",
                  transition: "opacity 0.6s ease, transform 0.6s ease",
                }}
              >
                <span className="text-white text-4xl xl:text-5xl font-light tracking-tight">
                  {stat.value}
                </span>
                <span
                  className="text-white/40 text-xs uppercase tracking-[.25em]"
                  style={{ fontFamily: "Muli, sans-serif" }}
                >
                  {stat.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── INSTAGRAM ────────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 text-center py-20 transition bg-black border-t border-white/10">
        <h2
          className="xl:text-4xl lg:text-4xl md:text-3xl text-2xl tracking-[.25em] uppercase mb-5 text-white"
        >
          Follow Us on Instagram
        </h2>
        <p className="mb-2 text-white/60 italic text-xl" style={{ fontFamily: "Muli, sans-serif" }}>
          <a href="https://www.instagram.com/glintzweddingphotography?igsh=MTJibTkyZzhjczljeg==" className="hover:text-white transition">
            @glintzweddingphotography
          </a>
        </p>
        <p className="text-white/60 italic text-xl" style={{ fontFamily: "Muli, sans-serif" }}>
          <a href="https://instagram.com/glintz_studios" className="hover:text-white transition">
            @glintz_studios
          </a>
        </p>
      </div>

      <Footer />
    </>
  );
};

export default PortfolioAll;